import { defineMessages } from '@edx/frontend-platform/i18n';

const messages = defineMessages({
  'profile.viewMyRecords': {
    id: 'profile.viewMyRecords',
    defaultMessage: 'View My Records',
    description: 'A link to go view my academic records',
  },
  'profile.name.full.name': {
    id: 'profile.name.full.name',
    defaultMessage: 'Full Name',
    description: 'A section of a user profile',
  },
  'profile.user.gender': {
    id: 'profile.user.gender',
    defaultMessage: 'Gender',
    description: 'A section of a gender profile',
  },
  'profile.user.gender.placeholder': {
    id: 'profile.user.gender.placeholder',
    defaultMessage: 'Fill in your gender information',
    description: 'A placeholder of a gender profile section',
  },
  'profile.loading': {
    id: 'profile.loading',
    defaultMessage: 'Profile loading...',
    description: 'Message displayed when the profile data is loading.',
  },
  'profile.country.label': {
    id: 'profile.country.label',
    defaultMessage: 'Location (country)',
    description: 'The label for a country in a user profile.',
  },
  profileTitle: {
    id: 'profile.title',
    defaultMessage: 'My profile',
  },
  profileTitleEditing: {
    id: 'profile.title.editing',
    defaultMessage: 'Edit profile',
  },
  profileSubtitle: {
    id: 'profile.subtitle',
    defaultMessage:
      'Fill in your information. This will help us provide even better course recommendations for you.',
  },
  editButton: {
    id: 'profile.button.edit',
    defaultMessage: 'Edit',
  },
  saveButton: {
    id: 'profile.button.save',
    defaultMessage: 'Save changes',
  },
  cancelButton: {
    id: 'profile.button.cancel',
    defaultMessage: 'Cancel and quit',
  },
  nameDescription: {
    id: 'profile.name.description',
    defaultMessage: 'To change your name, go to { accountLink }',
  },
  userAccountLink: {
    id: 'profile.account.link',
    defaultMessage: 'Account settings',
  },
  timeWithUsYears: {
    id: 'profile.timeWithUs.years',
    defaultMessage:
      '{count, plural, one {{count} year with Prometheus} other {{count} years with Prometheus}}',
  },
  timeWithUsMonths: {
    id: 'profile.timeWithUs.months',
    defaultMessage:
      '{count, plural, one {{count} month with Prometheus} other {{count} months with Prometheus}}',
  },
  timeWithUsDays: {
    id: 'profile.timeWithUs.days',
    defaultMessage:
      '{count, plural, one {{count} day with Prometheus} other {{count} days with Prometheus}}',
  },
  userProfession: {
    id: 'profile.user.profession',
    defaultMessage: 'My profession',
  },
  selectOptionDefault: {
    id: 'profile.select.option.default',
    defaultMessage: 'Select gender',
  },
  selectOptionMale: {
    id: 'profile.select.option.male',
    defaultMessage: 'Male',
  },
  selectOptionFemale: {
    id: 'profile.select.option.female',
    defaultMessage: 'Female',
  },
  selectOptionOther: {
    id: 'profile.select.option.other',
    defaultMessage: 'Other / Prefer not to say',
  },
  userInterestsTitle: {
    id: 'profile.user.interests.title',
    defaultMessage: 'Which categories of courses are you interested in?',
  },
  userInterestsPlaceholder: {
    id: 'profile.user.interests.placeholder',
    defaultMessage: 'Fill in the information about the categories of courses you are interested in',
  },
  userInterestsEditButton: {
    id: 'profile.user.interests.edit.button',
    defaultMessage: 'Add categories',
  },
  userSkillsTitle: {
    id: 'profile.user.skills.title',
    defaultMessage: 'What skills do you want to develop?',
  },
  userSkillsModalTitle: {
    id: 'profile.user.skills.modal.title',
    defaultMessage: 'What skills do you plan to develop in the next 6 months?',
  },
  userSkillsPlaceholder: {
    id: 'profile.user.skills.placeholder',
    defaultMessage: 'Fill in the information about the skills you want to develop',
  },
  userSkillsEditButton: {
    id: 'profile.user.skills.edit.button',
    defaultMessage: 'Add skills',
  },
  certificateInstructionsTitle: {
    id: 'profile.certificate.instructions.title',
    defaultMessage:
      "Don't leave your achievements unnoticed, add your Prometheus PLUS certificates to LinkedIn:",
  },
  certificateStep1Title: {
    id: 'profile.certificate.step1.title',
    defaultMessage: 'Prepare your data',
  },
  certificateStep2Title: {
    id: 'profile.certificate.step2.title',
    defaultMessage: 'Add the certificate to LinkedIn',
  },
  certificateStep3Title: {
    id: 'profile.certificate.step3.title',
    defaultMessage: 'Final step',
  },
  certificateStep1Intro: {
    id: 'profile.certificate.step1.intro',
    defaultMessage:
      'Scan the <bold>QR code</bold> on your certificate. On the page that opens, you will find everything you need:',
  },
  certificateStep1CertId: {
    id: 'profile.certificate.step1.certId',
    defaultMessage: '<bold>Certificate ID</bold> (a text code, for example: <code>eb66a1…</code>).',
  },
  certificateStep1Url: {
    id: 'profile.certificate.step1.url',
    defaultMessage:
      '<bold>Link (URL)</bold> to this page — copy it from the browser address bar (looks like <urlSpan>https://certs.prometheus.org.ua…</urlSpan>).',
  },
  certificateStep2Intro: {
    id: 'profile.certificate.step2.intro',
    defaultMessage:
      'Go to your profile, scroll to the <bold>«Licenses & certifications»</bold> section and click <bold>«+»</bold>:',
  },
  certificateStep2Name: {
    id: 'profile.certificate.step2.name',
    defaultMessage: '<bold>Name:</bold> the name of your course.',
  },
  certificateStep2Org: {
    id: 'profile.certificate.step2.org',
    defaultMessage:
      '<bold>Organization:</bold> start typing <bold>Prometheus</bold> and select the option with the orange logo.',
  },
  certificateStep2CertId: {
    id: 'profile.certificate.step2.certId',
    defaultMessage:
      '<bold>Certificate ID:</bold> paste the number found via QR code (for example: <code>eb66a1…</code>).',
  },
  certificateStep2VerificationUrl: {
    id: 'profile.certificate.step2.verificationUrl',
    defaultMessage:
      '<bold>Credential URL:</bold> paste the copied link to the verification page (<urlSpan>https://certs.prometheus.org.ua…</urlSpan>).',
  },
  certificateStep3Media: {
    id: 'profile.certificate.step3.media',
    defaultMessage: '<bold>Add media:</bold> upload the certificate file (PDF or screenshot).',
  },
  certificateStep3Skills: {
    id: 'profile.certificate.step3.skills',
    defaultMessage: '<bold>Skills:</bold> add 2–3 key skills you have acquired.',
  },
  certificateStep3Save: {
    id: 'profile.certificate.step3.save',
    defaultMessage:
      '<bold>Click «Save»</bold> — and your achievement is officially confirmed in your profile.',
  },
  certificateStep3Tag: {
    id: 'profile.certificate.step3.tag',
    defaultMessage:
      'Tag the Prometheus account in your post, we will be happy to hear your feedback!',
  },
});

export default messages;
